import React, { useState, useEffect } from 'react';
import { Clock, Info, BookOpen, GraduationCap, ChevronRight } from 'lucide-react';
import { useAuth } from './auth-context';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './auth-context';

const EXPERIENCE_OPTIONS = [
    {
        key: 'experienced',
        label: 'I have IT experience',
        description: 'You have hands-on IT experience (2+ years in administration, networking, helpdesk, or similar)',
        hours: 60,
        resources: [
            { name: 'CompTIA Security+ Objectives (SY0-701)', type: 'PDF' },
            { name: 'Professor Messer SY0-701 Videos', type: 'Video' },
            { name: 'CertCrammer Practice Tests', type: 'Practice' },
        ]
    },
    {
        key: 'beginner',
        label: 'New to IT',
        description: 'You\'re starting from scratch or have minimal technical background',
        hours: 120,
        resources: [
            { name: 'CompTIA Security+ Study Guide', type: 'Book' },
            { name: 'Professor Messer SY0-701 Videos', type: 'Video' },
            { name: 'CompTIA Network+ Basics (optional)', type: 'Prereq' },
            { name: 'CertCrammer Practice Tests', type: 'Practice' },
            { name: 'CompTIA Security+ Objectives (SY0-701)', type: 'PDF' },
            { name: 'Hands-on labs (TryHackMe, etc.)', type: 'Lab' },
        ]
    }
];

const StudyTimeCard = ({ userStats }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [experienceLevel, setExperienceLevel] = useState(null);
    const [showExperienceModal, setShowExperienceModal] = useState(false);
    const { user } = useAuth();

    useEffect(() => {
        const loadExperience = async () => {
            if (!user) return;
            try {
                const userRef = doc(db, 'users', user.uid);
                const userDoc = await getDoc(userRef);
                if (userDoc.exists() && userDoc.data().experienceLevel) {
                    setExperienceLevel(userDoc.data().experienceLevel);
                }
            } catch (err) {
                console.error('Error loading experience level:', err);
            }
        };
        loadExperience();
    }, [user]);

    const saveExperience = async (key) => {
        setExperienceLevel(key);
        setShowExperienceModal(false);
        if (user) {
            try {
                const userRef = doc(db, 'users', user.uid);
                await updateDoc(userRef, {
                    experienceLevel: key,
                    lastUpdated: serverTimestamp()
                });
            } catch (err) {
                console.error('Error saving experience level:', err);
            }
        }
    };

    const currentOption = EXPERIENCE_OPTIONS.find(o => o.key === experienceLevel);
    const recommendedHours = currentOption?.hours;
    const practiceHours = Math.floor(userStats.totalTime / 60);

    return (
        <>
            <div className={`rounded-2xl bg-white/5 p-6 ring-1 ring-white/10 backdrop-blur ${showTooltip ? 'relative z-[60]' : ''}`}>
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-white">Study Time</h3>
                    <Clock size={20} className="text-cyan-300" />
                </div>
                <div className="text-3xl font-bold">
                    {recommendedHours
                        ? <>{practiceHours}<span className="text-white/40 text-xl font-normal">/{recommendedHours}h</span></>
                        : <>{practiceHours}h</>
                    }
                </div>
                {recommendedHours && (
                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                        <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{
                                width: `${Math.min(100, (practiceHours / recommendedHours) * 100)}%`,
                                background: practiceHours >= recommendedHours
                                    ? 'linear-gradient(90deg, #22d3ee, #3b82f6)'
                                    : 'linear-gradient(90deg, #f59e0b, #22d3ee)'
                            }}
                        />
                    </div>
                )}
                <div className="relative">
                    <div
                        className="flex cursor-help items-center gap-1 text-sm text-white/70 mt-1"
                        onMouseEnter={() => setShowTooltip(true)}
                        onMouseLeave={() => setShowTooltip(false)}
                    >
                        {recommendedHours
                            ? `${practiceHours}h practiced / ${recommendedHours}h recommended`
                            : 'Total practice time'
                        }
                        <Info size={14} className="text-white/60" />
                    </div>

                    {/* Tooltip */}
                    <div
                        className={`fixed left-4 right-4 md:absolute md:left-0 md:right-auto top-auto md:top-full z-[100] md:w-80 rounded-xl bg-[#0B1224] p-4 text-sm text-white ring-1 ring-white/10 shadow-2xl transition-opacity duration-200 mt-2 ${showTooltip ? 'opacity-100' : 'opacity-0 pointer-events-none'
                            }`}
                    >
                        {currentOption ? (
                            <div className="space-y-3">
                                <div>
                                    <div className="font-medium text-white/90 mb-1">Your study plan</div>
                                    <div className="text-white/60 text-xs">Based on your experience: <span className="text-cyan-400">{currentOption.label}</span></div>
                                </div>
                                <div>
                                    <div className="font-medium text-white/90 mb-1.5 flex items-center gap-1.5">
                                        <BookOpen size={13} />
                                        Recommended Resources
                                    </div>
                                    <ul className="space-y-1.5">
                                        {currentOption.resources.map((r, i) => (
                                            <li key={i} className="flex items-center gap-2 text-xs text-white/60">
                                                <span className="px-1.5 py-0.5 rounded bg-white/10 text-white/50 text-[10px] font-medium">{r.type}</span>
                                                {r.name}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <button
                                    onClick={(e) => { e.stopPropagation(); setShowTooltip(false); setShowExperienceModal(true); }}
                                    className="text-xs text-cyan-400/70 hover:text-cyan-400 transition-colors"
                                >
                                    Change experience level
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <div className="text-white/60">
                                    Recommended: ~60h for those with IT experience, ~120h for beginners.
                                </div>
                                <button
                                    onClick={(e) => { e.stopPropagation(); setShowTooltip(false); setShowExperienceModal(true); }}
                                    className="flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 transition-colors font-medium"
                                >
                                    Set your experience level for personalized goals
                                    <ChevronRight size={12} />
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Prompt to set experience if not set */}
                {!experienceLevel && (
                    <button
                        onClick={() => setShowExperienceModal(true)}
                        className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs text-cyan-400/70 hover:text-cyan-400 transition-colors bg-cyan-500/5 hover:bg-cyan-500/10 rounded-lg py-1.5 ring-1 ring-cyan-500/15"
                    >
                        <GraduationCap size={13} />
                        Set experience level
                    </button>
                )}
            </div>

            {/* Experience Level Modal */}
            {showExperienceModal && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
                    <div className="rounded-2xl bg-[#0B1224] ring-1 ring-white/10 max-w-lg w-full p-6 shadow-2xl">
                        <div className="flex items-center gap-3 mb-1">
                            <div className="h-10 w-10 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                                <GraduationCap className="h-5 w-5 text-cyan-400" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-white">What's your experience level?</h3>
                                <p className="text-sm text-white/55">This helps us personalize your study plan</p>
                            </div>
                        </div>
                        <div className="space-y-3 mt-5">
                            {EXPERIENCE_OPTIONS.map((opt) => (
                                <button
                                    key={opt.key}
                                    onClick={() => saveExperience(opt.key)}
                                    className={`w-full text-left p-4 rounded-xl ring-1 transition-colors ${experienceLevel === opt.key
                                        ? 'bg-cyan-500/10 ring-cyan-500/40'
                                        : 'bg-white/5 ring-white/10 hover:bg-white/10'
                                        }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="font-medium text-white">{opt.label}</div>
                                        <span className="text-sm text-cyan-400 font-medium">~{opt.hours}h</span>
                                    </div>
                                    <div className="text-sm text-white/55 mt-1">{opt.description}</div>
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={() => setShowExperienceModal(false)}
                            className="mt-4 w-full p-2 rounded-xl ring-1 ring-white/10 text-white/55 hover:bg-white/10 text-sm"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default StudyTimeCard;